import type { ReactElement } from "react";

import type { AgeDistribution } from "../api/types";

// One labelled column of the stats table (a group: overall / a protocol / a
// substudy) and its distribution.
export interface AgeStatsGroup {
  key: string;
  label: string;
  distribution: AgeDistribution;
}

// The no-photos distribution — one copy, so adding a statistic to
// AgeDistribution means updating exactly one literal.
export const EMPTY_DISTRIBUTION: AgeDistribution = {
  n: 0,
  mean: null,
  std: null,
  min: null,
  p25: null,
  median: null,
  p75: null,
  max: null,
  iqr: null,
  range: null,
};

// Statistic rows, in the order of the reference table. `n` prints as an integer;
// every other statistic is a FaceAge value shown to two decimals.
const STAT_ROWS: { key: keyof AgeDistribution; label: string }[] = [
  { key: "n", label: "N (Photos)" },
  { key: "mean", label: "Mean" },
  { key: "std", label: "Std" },
  { key: "min", label: "Min" },
  { key: "p25", label: "P25" },
  { key: "median", label: "Median" },
  { key: "p75", label: "P75" },
  { key: "max", label: "Max" },
  { key: "iqr", label: "IQR" },
  { key: "range", label: "Range" },
];

function cell(value: number | null, isCount: boolean): string {
  if (value === null) return "—";
  return isCount ? String(value) : value.toFixed(2);
}

// FaceAge summary statistics as a stat-rows × group-columns table. Used by the
// admin overview (overall + each protocol + each substudy) and the collaborator
// overview (its one substudy).
export function AgeStatsTable({ groups }: { groups: AgeStatsGroup[] }): ReactElement {
  const anyPhotos = groups.some((g) => g.distribution.n > 0);
  if (!anyPhotos) {
    return <p className="muted">No processed photos yet.</p>;
  }
  return (
    <div className="table-scroll">
      <table className="data-table stats-table">
        <thead>
          <tr>
            <th scope="col">Statistic</th>
            {groups.map((g) => (
              <th key={g.key} scope="col" className="num">
                {g.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {STAT_ROWS.map((row) => (
            <tr key={row.key}>
              <th scope="row" className="muted">
                {row.label}
              </th>
              {groups.map((g) => (
                <td key={g.key} className="num">
                  {cell(g.distribution[row.key], row.key === "n")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
